import { useCallback, useEffect, useRef, useState } from "react";
import globalAxios from "axios";
import {
	EMPTY_OVERVIEW,
	normalizePublishItem,
	normalizeOverview,
	normalizePreviewStatus,
	type PreviewStatus,
	type ProductionOverview,
	type ProductionPublishItem
} from "./publicationUtils";

/** Page size of « afficher plus » (mirrors the server cap of the overview). */
export const GROUP_PAGE_SIZE = 25;

/** GET /api/deployment/production/overview/items : one page of a group's pending rows. */
export const fetchGroupItems = async (contentTypeId: string, offset: number, limit: number) => {
	const res = await globalAxios.get(`/api/deployment/production/overview/items`, {
		params: { contentTypeId, offset, limit },
		withCredentials: true,
		headers: { Accept: "application/json" }
	});
	const raw = res.data ?? {};
	const items: ProductionPublishItem[] = Array.isArray(raw.items) ? raw.items.map(normalizePublishItem) : [];
	return { items, total: Number(raw.total ?? 0) };
};

/** Merges a page of extra items into a group (dedup by queue id), keeping the newest-first order. */
const appendGroupItems = (overview: ProductionOverview, contentTypeId: string, items: ProductionPublishItem[]): ProductionOverview => ({
	...overview,
	groups: overview.groups.map((g) => {
		if (g.contentTypeId !== contentTypeId) return g;
		const seen = new Set(g.items.map((i) => i.id));
		return { ...g, items: [...g.items, ...items.filter((i) => !seen.has(i.id))] };
	})
});

/**
 * Loads the aggregated production view (GET /api/deployment/production/overview) : failures
 * first, pending changes grouped by content type. Uses the app's shared axios instance
 * (baseURL + Auth0 interceptor), exactly like the rest of the admin's bespoke calls.
 */
export const useProductionOverview = () => {
	const [overview, setOverview] = useState<ProductionOverview>(EMPTY_OVERVIEW);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const loadedOnce = useRef(false);
	// Extra pages already opened per group (« afficher plus ») : re-attached after every silent
	// refresh so a publish never folds the list the operator has just expanded.
	const extraByType = useRef<Record<string, number>>({});
	const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});

	const load = useCallback(async (cacheBust?: number) => {
		// Only the very first load shows the page-level skeleton; refreshes are silent.
		if (!loadedOnce.current) setLoading(true);
		setError(null);
		try {
			const res = await globalAxios.get(`/api/deployment/production/overview`, {
				withCredentials: true,
				headers: { Accept: "application/json" },
				...(cacheBust != null && { params: { _: cacheBust } })
			});
			let next = normalizeOverview(res.data);
			for (const [typeId, extra] of Object.entries(extraByType.current)) {
				const base = next.groups.find((g) => g.contentTypeId === typeId);
				if (extra <= 0 || !base || base.count <= base.items.length) continue;
				try {
					const page = await fetchGroupItems(typeId, base.items.length, extra);
					next = appendGroupItems(next, typeId, page.items);
				} catch {
					/* the extra page is best effort: the base overview is already there */
				}
			}
			setOverview(next);
			loadedOnce.current = true;
		} catch (err) {
			console.error(err);
			setError("Impossible de charger l'état de la production.");
		} finally {
			setLoading(false);
		}
	}, []);

	/** Loads the next page of a group's items and appends it (« afficher N de plus »). */
	const loadMore = useCallback(
		async (contentTypeId: string) => {
			const group = overview.groups.find((g) => g.contentTypeId === contentTypeId);
			if (!group) return;
			setLoadingMore((prev) => ({ ...prev, [contentTypeId]: true }));
			try {
				const page = await fetchGroupItems(contentTypeId, group.items.length, GROUP_PAGE_SIZE);
				extraByType.current[contentTypeId] = (extraByType.current[contentTypeId] ?? 0) + page.items.length;
				setOverview((prev) => appendGroupItems(prev, contentTypeId, page.items));
			} catch (err) {
				console.error(err);
			} finally {
				setLoadingMore((prev) => ({ ...prev, [contentTypeId]: false }));
			}
		},
		[overview.groups]
	);

	return { overview, loading, error, load, loadMore, loadingMore };
};

/**
 * Polls GET /api/deployment/preview/status (the automatic preview publishing loop) every
 * `pollMs` ms. `refresh` re-reads immediately : the pill calls it when the "preview-sync"
 * SignalR event lands so the state flips without waiting for the next tick.
 */
export const usePreviewStatus = (pollMs = 10000) => {
	const [status, setStatus] = useState<PreviewStatus | null>(null);
	const mountedRef = useRef(true);

	const refresh = useCallback(async () => {
		try {
			const res = await globalAxios.get(`/api/deployment/preview/status`, {
				withCredentials: true,
				headers: { Accept: "application/json" }
			});
			if (mountedRef.current) setStatus(normalizePreviewStatus(res.data));
		} catch (err) {
			console.error(err);
			// Keep the last known status : a transient fetch error must not flash the pill red.
		}
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		refresh();
		const timer = setInterval(refresh, pollMs);
		return () => {
			mountedRef.current = false;
			clearInterval(timer);
		};
	}, [refresh, pollMs]);

	return { status, refresh };
};
